import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ConfigContext } from '@/components';
import { useApplicationsList, useCurrentApplication } from '@/components/ApplicationContext';
import { constants } from '@/constants';
import { Box, CircularProgress, Container, Link, Paper, Typography, useTheme } from '@mui/material';
import axios from 'axios';
import Cookies from 'js-cookie';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import FrameImage from '../../../public/FrameImage.jpg';
import './HelpCenterGuideViewer.css';
import authenticatedRoute from '@/views/AuthenticatedRoute';
import { useThemeDetails } from '@/components/ThemeDetailsContext';
import { convertVideoLinksToPlayers, highlightSearchInElement } from '@/utils/helpCenterGuideViewerUtils';
const parseHtmlIntoSections = (htmlContent) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const container = doc.body;
    const h1Elements = Array.from(container.querySelectorAll('h1'));
    if (h1Elements.length === 0) {
        return [
            {
                title: '',
                content: container.innerHTML,
                id: 'section-0'
            }
        ];
    }
    const getSectionContent = (parentContainer, currentH1, nextH1) => {
        const allElements = Array.from(parentContainer.children);
        const startIndex = allElements.indexOf(currentH1);
        const endIndex = nextH1
            ? allElements.indexOf(nextH1)
            : allElements.length;
        if (startIndex === -1) {
            return currentH1.outerHTML;
        }
        const sectionElements = allElements.slice(startIndex, endIndex);
        return sectionElements.map((el) => el.outerHTML).join('');
    };
    return h1Elements.map((h1, index) => {
        const title = h1.textContent?.trim() || '';
        const nextH1 = h1Elements[index + 1];
        const content = getSectionContent(container, h1, nextH1);
        return {
            title,
            content,
            id: `section-${index + 1}`
        };
    });
};
const HelpCenterGuideViewer = () => {
    const { stateImages } = useThemeDetails();
    const [sections, setSections] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const sectionRefs = useRef({});
    const sectionContentRefs = useRef({});
    const [applicationsList, setApplicationsList] = useState([]);
    const theme = useTheme();
    const { configState } = useContext(ConfigContext);
    const [isLoadingApplications, setIsLoadingApplications] = useState(true);
    const [fallbackApplication, setFallbackApplication] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [bg, setBg] = useState('');
    const productId = Cookies.get('Product Selected');
    const user = Cookies.get('user');
    const selectedLanguage = useMemo(() => {
        try {
            return user
                ? JSON.parse(user)?.details?.selected_language || 'en'
                : 'en';
        }
        catch {
            return 'en';
        }
    }, [user]);
    const getApplications = useApplicationsList();
    const currentApp = useCurrentApplication(productId ? Number(productId) : 0);
    const currentApplication = useMemo(() => {
        const sessionApplication = sessionStorage.getItem('current_application');
        if (sessionApplication) {
            try {
                return JSON.parse(sessionApplication);
            }
            catch {
                return currentApp;
            }
        }
        return currentApp;
    }, [currentApp]);
    const resolvedApplication = useMemo(() => currentApplication ?? fallbackApplication, [currentApplication, fallbackApplication]);
    const selectedHelpCenterUrl = useMemo(() => {
        return (resolvedApplication?.details?.help_center?.[selectedLanguage] || '');
    }, [resolvedApplication, selectedLanguage]);
    const englishHelpCenterUrl = useMemo(() => {
        return resolvedApplication?.details?.help_center?.en || '';
    }, [resolvedApplication]);
    useEffect(() => {
        let blobUrl = null;
        const loadBackgroundImage = async () => {
            try {
                const response = await axios.get(FrameImage, {
                    responseType: 'blob'
                });
                blobUrl = URL.createObjectURL(response.data);
                setBg(`url(${blobUrl})`);
            }
            catch {
                setBg('');
            }
        };
        void loadBackgroundImage();
        return () => {
            if (blobUrl) {
                URL.revokeObjectURL(blobUrl);
            }
        };
    }, []);
    useEffect(() => {
        const loadApplications = async () => {
            try {
                const apps = await getApplications();
                setApplicationsList(apps);
            }
            finally {
                setIsLoadingApplications(false);
            }
        };
        void loadApplications();
    }, [getApplications]);
    useEffect(() => {
        if (isLoadingApplications) {
            return;
        }
        if (applicationsList.length > 0 && productId && currentApplication) {
            setIsInitialized(true);
            return;
        }
        if (applicationsList.length > 0) {
            const appByName = applicationsList.find((app) => app?.details?.baseApp?.toLowerCase() === 'true');
            if (appByName) {
                setFallbackApplication(appByName);
            }
            setIsInitialized(true);
            return;
        }
        setIsInitialized(true);
    }, [applicationsList, currentApplication, isLoadingApplications, productId]);
    useEffect(() => {
        if (!isInitialized) {
            return;
        }
        const loadContentWithFallback = async () => {
            try {
                const urlToFetch = selectedHelpCenterUrl || englishHelpCenterUrl;
                if (!urlToFetch) {
                    throw new Error(constants.help_center_content_not_available);
                }
                let response;
                try {
                    response = await axios.get(urlToFetch, {
                        responseType: 'text'
                    });
                }
                catch {
                    if (selectedLanguage !== 'en' && englishHelpCenterUrl) {
                        try {
                            response = await axios.get(englishHelpCenterUrl, {
                                responseType: 'text'
                            });
                        }
                        catch {
                            throw new Error(constants.help_center_content_not_available);
                        }
                    }
                    else {
                        throw new Error(constants.help_center_content_not_available);
                    }
                }
                const rawContent = convertVideoLinksToPlayers(String(response.data));
                setSections(parseHtmlIntoSections(rawContent));
            }
            catch (error) {
                setSections([
                    {
                        title: constants.help_center_error_title,
                        content: `<h1>${error.message}</h1>`,
                        id: 'error-section'
                    }
                ]);
            }
        };
        void loadContentWithFallback();
    }, [
        englishHelpCenterUrl,
        isInitialized,
        selectedHelpCenterUrl,
        selectedLanguage
    ]);
    useEffect(() => {
        for (const section of sections) {
            const element = sectionContentRefs.current[section.id];
            if (element) {
                highlightSearchInElement(element, searchTerm);
            }
        }
    }, [sections, searchTerm]);
    const scrollToSection = (sectionId) => {
        const element = sectionRefs.current[sectionId];
        if (element) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    };
    const setSectionRef = (sectionId) => (el) => {
        sectionRefs.current[sectionId] = el;
    };
    const setSectionContentRef = (sectionId) => (el) => {
        sectionContentRefs.current[sectionId] = el;
    };
    const hasValidSections = sections.some((section) => Boolean(section.title.trim()) && section.id !== 'error-section');
    const renderIndexContent = () => (_jsxs(Box, { className: "help-center-index-container", children: [_jsx(Typography, { variant: "h4", component: "h1", gutterBottom: true, sx: {
                    color: theme.palette.text.primary,
                    mb: 2,
                    alignSelf: 'flex-start'
                }, children: constants.help_center_index }), _jsx(Box, { sx: { width: '100%', pl: 0 }, children: sections.map((section) => (_jsx(Box, { sx: { mb: 2 }, children: _jsx(Link, { component: "button", onClick: () => scrollToSection(section.id), className: "help-center-index-link", sx: {
                            color: configState?.settings?.button_color ===
                                '#FFFFFF'
                                ? '#000000'
                                : configState?.settings?.button_color,
                            ...theme.typography.h6
                        }, children: section.title }) }, section.id))) })] }));
    const renderHeaderWithImage = () => (_jsx(Box, { className: "help-center-header-container", sx: {
            '&::before': {
                backgroundImage: bg
            }
        }, children: _jsx(Box, { className: "help-center-header-overlay", sx: {
                background: configState?.settings?.button_color ||
                    theme.palette.primary.main
            }, children: _jsx(Box, { className: "help-center-header-title-container", children: _jsx(Typography, { variant: "h2", component: "h2", className: "help-center-header-title", sx: {
                        color: configState?.settings?.button_color ===
                            '#FFFFFF'
                            ? '#000000'
                            : '#FFFFFF'
                    }, children: constants.help_center_title }) }) }) }));
    if (isLoadingApplications) {
        return (_jsx(Box, { className: "help-center-loading-container", children: stateImages?.Loading ? (_jsx(Box, { component: "img", src: stateImages.Loading, alt: "Loading", sx: { width: 128, height: 128 } })) : (_jsx(CircularProgress, { size: 60 })) }));
    }
    return (_jsx(Container, { maxWidth: "lg", className: "help-center-main-container", children: _jsxs(Box, { className: "help-center-container", children: [_jsx(Paper, { ref: setSectionRef('top-section'), elevation: 1, className: "help-center-header-section", children: renderHeaderWithImage() }), _jsx(Box, { sx: { my: 2 }, children: _jsx("input", { type: "text", placeholder: constants.help_center_search_placeholder, value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), style: {
                            width: '100%',
                            padding: '12px 16px',
                            fontSize: '16px',
                            border: `2px solid ${theme.palette.divider}`,
                            borderRadius: '8px',
                            fontFamily: 'inherit',
                            boxSizing: 'border-box',
                            outline: 'none',
                            transition: 'border-color 0.2s'
                        }, onFocus: (e) => {
                            e.currentTarget.style.borderColor =
                                theme.palette.primary.main;
                        }, onBlur: (e) => {
                            e.currentTarget.style.borderColor =
                                theme.palette.divider;
                        } }) }), hasValidSections && (_jsx(Paper, { ref: setSectionRef('index-section'), elevation: 1, className: "help-center-index-section", children: renderIndexContent() })), sections.map((section) => (_jsx(Paper, { id: section.id, elevation: 1, ref: setSectionRef(section.id), className: "help-center-section", sx: {
                        '& h1': {
                            ...theme.typography.h4,
                            color: theme.palette.text.primary
                        },
                        '& h2': {
                            ...theme.typography.h5,
                            color: theme.palette.text.primary
                        },
                        '& h3': {
                            ...theme.typography.h6,
                            color: theme.palette.text.primary
                        },
                        '& p': {
                            ...theme.typography.body1
                        },
                        '& a': {
                            color: configState?.settings?.button_color ||
                                theme.palette.primary.main
                        },
                        '& strong, & b': {
                            color: theme.palette.text.primary
                        },
                        '& iframe': {
                            width: '100%',
                            minHeight: '400px',
                            border: 0,
                            borderRadius: '8px',
                            display: 'block'
                        },
                        '& video': {
                            width: '100%',
                            display: 'block',
                            borderRadius: '8px'
                        },
                        '& mark': {
                            backgroundColor: '#ffeb3b',
                            padding: 0
                        }
                    }, children: _jsx(Box, { ref: setSectionContentRef(section.id), dangerouslySetInnerHTML: {
                            __html: section.content
                        } }) }, section.id)))] }) }));
};
export default authenticatedRoute(HelpCenterGuideViewer);
